import Alert from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { ApolloError } from "@apollo/client";
import { App, AppTable as AppTableType } from "../../types/app.type";
import { AppTable } from "./AppTable";
import { columns } from "./Columns";

type Errors = {
  organizationAppsError?: ApolloError;
  publicAppsError?: ApolloError;
};

type Props = {
  apps: App[];
  loading: boolean;
  errors: Errors;
};

export const AppTableView = ({
  apps,
  loading,
  errors: { organizationAppsError, publicAppsError }
}: Props) => {
  const { toast } = useToast();
  const tableAppData: AppTableType[] = apps.map((app) => {
    const base = {
      __typename: app.__typename,
      id: app.id,
      displayName: app.displayName,
      description: app.description,
      created: app.createdAt,
      developer: app.createdBy.fullName,
      appImage: app.pictureUrl,
      origin: app.origin,
      defaultDeployment: app.defaultDeployment
    };

    switch (app.origin) {
      case "public":
        return {
          ...base,
          origin: "public",
          link: app.publicUrl
        };
      case "organization":
        return {
          ...base,
          origin: "organization",
          slug: app.slug,
          updatedAt: app.defaultDeployment?.current?.createdAt
        };
      case "subscription":
        return {
          ...base,
          origin: "subscription",
          subscription: {
            __typename: "AppSubscription",
            id: app.subscription?.id || ""
          },
          updatedAt: app.defaultDeployment?.current?.createdAt
        };
      default:
        return base;
    }
  });

  if (organizationAppsError) {
    toast({
      title: "Unable to load organization apps and subscription apps",
      description:
        "An error occurred while loading apps. Please try to refresh the page or report the issue."
    });
    console.error(organizationAppsError);
  }
  if (publicAppsError) {
    toast({
      title: "Unable to load public apps",
      description:
        "An error occurred while loading apps. Please try to refresh the page or report the issue."
    });
    console.error(organizationAppsError);
  }

  if (organizationAppsError && publicAppsError)
    return (
      <Alert
        title="Unable to load apps"
        description="An error occurred while loading apps. Please try to refresh the page or report the issue."
        data-testid="errorAlert"
      />
    );
  return <AppTable columns={columns} data={tableAppData} loading={loading} />;
};
